import {Component, Input, OnChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {countryNames, decimalInput, fieldError, fieldErrors, InvoicingService} from '../../shared/invoicing.service';
import {ClientBilling, InvoicingLists} from '../../interfaces/invoicing';

/** A client's billing details (#1516), Admin only. The document language stays on the client form. */
@Component({
  selector: 'bst-invoicing-client',
  templateUrl: './invoicing-client.component.html',
  styleUrls: ['./invoicing-form.scss'],
  standalone: false
})
export class InvoicingClientComponent implements OnChanges {
  @Input() clientId: number;
  // A deep link from an invoice issue (?field=town); focused once the billing details load.
  @Input() focusField: string | null = null;
  billing: ClientBilling = null;
  lists: InvoicingLists = null;
  countries: {code: string, name: string}[] = [];
  errors: Record<string, string[]> = {};
  busy = false;

  constructor(private invoicing: InvoicingService, private translate: TranslateService) {}

  // The client page is reused from one client to the next, so load on every new id.
  async ngOnChanges() {
    const id = this.clientId;
    this.billing = null;
    this.errors = {};
    const [billing, lists] = await Promise.all([this.invoicing.getClient(id), this.invoicing.getLists()])
      .catch((): [ClientBilling, InvoicingLists] => [null, null]);
    if (id !== this.clientId) { return; }
    [this.billing, this.lists] = [billing, lists];
    if (this.lists) { this.countries = countryNames(this.lists.countries, this.translate.currentLang); }
    this.focus(this.focusField);
  }

  focus(field: string | null) {
    if (!field || !this.billing) { return; }
    // After this render: the field may have only just appeared.
    setTimeout(() => {
      const el = document.getElementById('client-' + field);
      (el?.querySelector('button') ?? el)?.focus();
    });
  }

  decimal(value: string | null | undefined): string {
    return decimalInput(this.translate, value);
  }

  error(field: string): string | null {
    return fieldError(this.translate, this.errors, field);
  }

  async save() {
    if (this.busy) { return; }
    this.busy = true;
    this.errors = {};
    const saved = await this.invoicing.saveClient(this.clientId, this.billing).catch(e => { this.errors = fieldErrors(e); return null; });
    this.busy = false;
    if (saved) { this.billing = saved; }
  }
}
